import { useChat } from "@ai-sdk/react";
import { HUB_BASE_URL } from "@constants/env";
import { agentStreamFetch } from "@features/session/helpers/agent-stream";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import { HUB_ENDPOINTS } from "@memorang/constants/urls";
import { DefaultChatTransport } from "ai";
import { useEffect, useMemo, useRef } from "react";
import type { SessionInsightsUIMessage } from "../types/session-insights";

const transport = new DefaultChatTransport<SessionInsightsUIMessage>({
	api: `${HUB_BASE_URL}${HUB_ENDPOINTS.SESSION_INSIGHTS}`,
	fetch: agentStreamFetch,
});

type Params = {
	sessionId: number;
	examName: string;
	examId: string;
	appId: string;
	tenantName: string;
	outputLanguage: string;
	enabled: boolean;
};

export const useSessionInsights = ({
	sessionId,
	examName,
	examId,
	appId,
	tenantName,
	outputLanguage,
	enabled,
}: Params) => {
	const { messages, sendMessage, stop, status } =
		useChat<SessionInsightsUIMessage>({
			transport,
			onError: (err) => {
				captureErrorToSentry(new Error("Session insights stream failed"), {
					error: serializeErrorForSentry(err),
				});
			},
		});

	const requestedSessionIdRef = useRef<number | null>(null);
	// biome-ignore lint/correctness/useExhaustiveDependencies: request payload is captured once per session, when it enables
	useEffect(() => {
		if (
			!(enabled && sessionId) ||
			requestedSessionIdRef.current === sessionId
		) {
			return;
		}
		requestedSessionIdRef.current = sessionId;
		sendMessage(
			{ text: "Generate" },
			{
				body: {
					sessionId,
					examName,
					examId,
					appId,
					tenantName,
					outputLanguage,
				},
			},
		);
	}, [enabled, sessionId, sendMessage]);

	const stopRef = useRef(stop);
	stopRef.current = stop;
	useEffect(
		() => () => {
			stopRef.current();
		},
		[],
	);

	const { summary, insights } = useMemo(() => {
		const last = messages[messages.length - 1];
		const parts = last?.role === "assistant" ? (last.parts ?? []) : [];
		const part = parts.filter((p) => p.type === "data-session-insights").at(-1);
		return {
			summary: part?.data.summary ?? "",
			insights: part?.data.insights ?? "",
		};
	}, [messages]);

	const requestActive = status === "submitted" || status === "streaming";
	// status is "ready" before kickoff; empty messages indicate pre-request pending
	const pending = enabled && (requestActive || messages.length === 0);

	return {
		summary,
		insights,
		loading: pending && !summary,
		insightsLoading: pending && !insights,
		streaming: requestActive,
		error: status === "error",
	};
};
